import React, { createContext, useContext, useState, useRef, ReactNode } from 'react'
import { createPortal } from 'react-dom'

import ConfirmDialog from 'components/ConfirmDialog'
import AlertDialog from 'components/AlertDialog'

interface ConfirmOptions {
  title?: string
  description: ReactNode
  confirmText?: string
  cancelText?: string
}

interface AlertOptions {
  title?: string
  description: ReactNode
  okText?: string
}

interface DialogContextType {
  confirm: (opts: ConfirmOptions) => Promise<boolean>
  alert: (opts: AlertOptions) => void
}

const DialogContext = createContext<DialogContextType | null>(null)

export const DialogProvider = ({ children }: { children: ReactNode }) => {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmOptions, setConfirmOptions] = useState<ConfirmOptions | null>(null)

  const [alertOpen, setAlertOpen] = useState(false)
  const [alertOptions, setAlertOptions] = useState<AlertOptions | null>(null)

  const resolveRef = useRef<((value: boolean) => void) | null>(null)

  const confirm = (opts: ConfirmOptions) => {
    setConfirmOptions(opts)
    setConfirmOpen(true)

    // returns a promise so we can use await with confirm to simulate the
    // same code flow that the native confirm has
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve
    })
  }

  const alert = (opts: AlertOptions) => {
    setAlertOptions(opts)
    setAlertOpen(true)
  }

  const handleConfirm = () => {
    if (resolveRef.current) resolveRef.current(true)

    setConfirmOpen(false)
  }

  const handleCancel = () => {
    if (resolveRef.current) resolveRef.current(false)

    setConfirmOpen(false)
  }

  const handleAlertClose = () => {
    setAlertOpen(false)
  }

  // setting contextValue on the provider allows us to retrieve it from the
  // useConfirmDialog and useAlertDialog functions
  const contextValue = { confirm, alert }

  return (
    <DialogContext.Provider value={contextValue}>
      {children}
      {
        /* createPortal adds the following to the DOM at the highest level to avoid any
           potential unwanted future conflicts with z-index or overflow from parent components */
        typeof document !== 'undefined' && createPortal(
          <>
            <ConfirmDialog
              isOpen={confirmOpen}
              title={confirmOptions?.title}
              description={confirmOptions?.description || ''}
              confirmText={confirmOptions?.confirmText}
              cancelText={confirmOptions?.cancelText}
              onCancel={handleCancel}
              onConfirm={handleConfirm}
            />
            <AlertDialog
              isOpen={alertOpen}
              title={alertOptions?.title}
              description={alertOptions?.description || ''}
              okText={alertOptions?.okText}
              onClose={handleAlertClose}
            />
          </>,
          document.body
        )
      }
    </DialogContext.Provider>
  )
}

export const useConfirmDialog = () => {
  const context = useContext(DialogContext)

  if (!context) {
    throw new Error('useConfirmDialog must be used within a DialogProvider')
  }

  return { confirm: context.confirm }
}

export const useAlertDialog = () => {
  const context = useContext(DialogContext)

  if (!context) {
    throw new Error('useAlertDialog must be used within a DialogProvider')
  }

  return { alert: context.alert }
}
